import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api-client";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";

/**
 * Page + formulaire de connexion express (téléphone + code OTP) — port de
 * apps/web/app/(account)/connexion/page.tsx et
 * apps/web/components/auth/LoginForm.tsx (fusionnés ici : ce SPA n'a pas de
 * Server Component distinguant page/formulaire, et `LoginForm` n'était de
 * toute façon utilisé QUE par cette page).
 *
 * VVS_MODELE_INTERFACE.md §1.12 : téléphone (+ email) → code OTP à 6 chiffres,
 * sans mot de passe (le téléphone est l'identifiant principal,
 * VVS_MODELE_DONNEES.md §3.7 — la première vérification OTP crée implicitement
 * le compte côté API, pas d'inscription séparée). Le code est envoyé par
 * email (plus jamais par SMS) : l'email est obligatoire à la toute première
 * connexion avec un numéro donné, mais l'API décide seule de cette obligation
 * — ce formulaire laisse volontairement le champ email optionnel et se
 * contente de relayer l'erreur de l'API si elle l'exige.
 *
 * Les deux étapes du parcours passent par TanStack Query (`useMutation`) au
 * lieu des Server Actions `requestOtpAction`/`verifyOtpAction` de
 * apps/web/lib/auth-actions.ts — appelées ici via
 * `useCustomerAuth().requestOtp()`/`verifyOtp()` (pas directement
 * `@/lib/api-client`) : c'est le contexte d'auth qui met à jour `user`/
 * `isLoggedIn` en cas de succès de `verifyOtp()` (cf. son propre commentaire
 * de tête), pas cette page.
 *
 * Redirection post-connexion : apps/web fait toujours `router.push("/compte")`
 * sans mémoriser la page d'origine (aucun `?next=`/`callbackUrl` dans la
 * source d'origine, vérifié par lecture directe). Ce SPA va légèrement
 * plus loin : `AccountRouteGuard` (cf. son commentaire) pose déjà
 * `state={{ from: location.pathname }}` en redirigeant vers `/connexion` —
 * autant l'honorer ici pour renvoyer l'utilisateur exactement là où il
 * voulait aller plutôt que systématiquement vers `/compte` (repli identique
 * à l'original si aucun `from` n'est présent, ex. visite directe de
 * `/connexion`).
 */

const E164_REGEX = /^\+[1-9]\d{7,14}$/;
const CODE_REGEX = /^\d{6}$/;
/**
 * Doit rester alignée sur `OTP_REQUEST_COOLDOWN_MS` côté apps/api
 * (auth/otp/otp.service.ts, 60 000 ms) : c'est ce délai que l'API impose
 * réellement avant d'accepter une nouvelle demande de code pour le même
 * numéro (429 sinon). Afficher un compte à rebours cohérent évite à
 * l'utilisateur de cliquer sur "renvoyer" trop tôt.
 */
const OTP_RESEND_COOLDOWN_SECONDS = 60;
/** Validation basique côté front — la validation faisant foi reste `@IsEmail()` côté API. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = "phone" | "code";

interface ConnexionLocationState {
  from?: string;
}

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem?.detail ?? err.problem?.title ?? err.message;
  }
  return "Une erreur réseau est survenue. Vérifiez votre connexion et réessayez.";
}

export function ConnexionPage() {
  const { isLoggedIn, requestOtp, verifyOtp } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("+225");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const requestOtpMutation = useMutation({
    mutationFn: ({ phoneE164, email: mail }: { phoneE164: string; email?: string }) =>
      requestOtp(phoneE164, mail),
  });
  const verifyOtpMutation = useMutation({
    mutationFn: ({ phoneE164, code: otpCode }: { phoneE164: string; code: string }) =>
      verifyOtp(phoneE164, otpCode),
  });
  const isPending = requestOtpMutation.isPending || verifyOtpMutation.isPending;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Déjà connecté : comportement identique à l'original (`redirect("/compte")`
  // côté serveur) — inutile de réafficher le formulaire de connexion.
  if (isLoggedIn) {
    return <Navigate to="/compte" replace />;
  }

  function handleRequestOtp(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const trimmed = phone.trim();
    if (!E164_REGEX.test(trimmed)) {
      setError("Entrez un numéro complet au format international, ex. +2250700000000.");
      return;
    }

    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
      setError("Entrez une adresse email valide, ou laissez le champ vide.");
      return;
    }

    if (!acceptsTerms) {
      setError("Merci d'accepter les CGV et les CGU pour continuer.");
      return;
    }

    requestOtpMutation.mutate(
      { phoneE164: trimmed, email: trimmedEmail || undefined },
      {
        onSuccess: (res) => {
          setInfoMessage(res.message);
          setDevCode(res.devCode ?? null);
          setStep("code");
          setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
        },
        onError: (err) => setError(toErrorMessage(err)),
      },
    );
  }

  function handleVerifyOtp(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const trimmedCode = code.trim();
    if (!CODE_REGEX.test(trimmedCode)) {
      setError("Le code contient 6 chiffres.");
      return;
    }

    verifyOtpMutation.mutate(
      { phoneE164: phone.trim(), code: trimmedCode },
      {
        onSuccess: () => {
          const state = location.state as ConnexionLocationState | null;
          navigate(state?.from ?? "/compte", { replace: true });
        },
        onError: (err) => setError(toErrorMessage(err)),
      },
    );
  }

  function handleChangePhone() {
    setStep("phone");
    setCode("");
    setError(null);
    setDevCode(null);
    setInfoMessage(null);
    setResendCooldown(0);
  }

  function handleResend() {
    if (resendCooldown > 0) return;
    setError(null);
    requestOtpMutation.mutate(
      { phoneE164: phone.trim(), email: email.trim() || undefined },
      {
        onSuccess: (res) => {
          setInfoMessage(res.message);
          setDevCode(res.devCode ?? null);
          setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
        },
        onError: (err) => setError(toErrorMessage(err)),
      },
    );
  }

  return (
    <div className="container-vvs flex justify-center py-12 lg:py-20">
      <div className="mx-auto w-full max-w-sm">
        <h1 className="font-display text-2xl font-bold text-navy">Se connecter</h1>
        <p className="mt-2 text-sm text-muted">
          Chez VVS, le téléphone est votre identifiant : aucun mot de passe, aucune inscription
          séparée. Un code vous est envoyé par email à chaque connexion.
        </p>

        {step === "phone" ? (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleRequestOtp} noValidate>
            <div className="flex flex-col gap-1">
              <label htmlFor="login-phone" className="text-xs font-medium uppercase tracking-wide text-navy">
                Numéro de téléphone
              </label>
              <input
                id="login-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                placeholder="+2250700000000"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                className="rounded-sm border border-line bg-white px-3 py-3 text-sm text-ink focus:border-gold focus:outline-none"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "login-error" : undefined}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="login-email" className="text-xs font-medium uppercase tracking-wide text-navy">
                Email
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="rounded-sm border border-line bg-white px-3 py-3 text-sm text-ink focus:border-gold focus:outline-none"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "login-error" : "login-email-hint"}
              />
              <p id="login-email-hint" className="text-xs text-muted">
                C&apos;est à cette adresse que le code de vérification est envoyé. Obligatoire
                uniquement lors de votre toute première connexion avec ce numéro — inutile de le
                ressaisir si vous l&apos;avez déjà renseigné.
              </p>
            </div>

            {/* Acceptation CGV/CGU — vérification purement front (pas de colonne
                `Customer` prévue pour ce lot pour horodater ce consentement côté
                serveur) ; bloque néanmoins réellement l'envoi tant que non cochée. */}
            <label className="flex items-start gap-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={acceptsTerms}
                onChange={(event) => setAcceptsTerms(event.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-line text-navy focus:ring-navy"
              />
              <span>
                J&apos;accepte les{" "}
                <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-golddark">
                  CGV
                </a>{" "}
                et les{" "}
                <a href="/cgu" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-golddark">
                  CGU
                </a>{" "}
                de VVS.
              </span>
            </label>

            {error ? (
              <p id="login-error" role="alert" className="text-sm text-red-700">
                {error}
              </p>
            ) : null}

            <Button type="submit" disabled={isPending || !acceptsTerms} className="w-full">
              {isPending ? "Envoi en cours…" : "Recevoir mon code"}
            </Button>
          </form>
        ) : (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleVerifyOtp} noValidate>
            <p className="text-sm text-ink">
              {email.trim() ? (
                <>
                  Code envoyé par email à <span className="font-medium">{email.trim()}</span> pour
                  le numéro <span className="font-medium">{phone}</span>.{" "}
                </>
              ) : (
                <>
                  Code envoyé par email à l&apos;adresse déjà enregistrée pour le numéro{" "}
                  <span className="font-medium">{phone}</span>.{" "}
                </>
              )}
              <button
                type="button"
                onClick={handleChangePhone}
                className="underline decoration-dotted underline-offset-2 hover:text-gold"
              >
                Changer de numéro
              </button>
            </p>

            <p className="text-xs text-muted">
              Vous ne trouvez pas l&apos;email ? Pensez à vérifier votre dossier « indésirables »/«
              spam » — il arrive que le code y soit filtré par erreur.
            </p>

            {infoMessage ? <p className="text-sm text-muted">{infoMessage}</p> : null}

            {devCode ? (
              <p className="rounded-sm border border-gold bg-cream2 px-3 py-2 text-sm text-navy">
                Mode test (envoi email désactivé côté API) — code : <span className="font-semibold">{devCode}</span>
              </p>
            ) : null}

            <div className="flex flex-col gap-1">
              <label htmlFor="login-code" className="text-xs font-medium uppercase tracking-wide text-navy">
                Code à 6 chiffres
              </label>
              <input
                id="login-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                className="rounded-sm border border-line bg-white px-3 py-3 text-center text-lg tracking-[0.5em] text-ink focus:border-gold focus:outline-none"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "login-error" : undefined}
              />
            </div>

            {error ? (
              <p id="login-error" role="alert" className="text-sm text-red-700">
                {error}
              </p>
            ) : null}

            <Button type="submit" disabled={isPending} className="w-full">
              {isPending ? "Vérification…" : "Valider le code"}
            </Button>

            <button
              type="button"
              onClick={handleResend}
              disabled={isPending || resendCooldown > 0}
              aria-live="polite"
              className="text-sm text-muted underline decoration-dotted underline-offset-2 hover:text-gold disabled:no-underline disabled:opacity-50"
            >
              {resendCooldown > 0
                ? `Je n'ai pas reçu de code — renvoyer dans ${resendCooldown}s`
                : "Je n'ai pas reçu de code — renvoyer"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
